// Retry only explicit API_STARTING responses for same-origin GET/HEAD reads.
const LIMIT = 30;
const WINDOW_MS = 60000;
const DELAY_MS = 2000;
const FLAG = '__conceitoStartupRetryInstalled';

export const isStartupRead = (method, url, origin, status, data) => {
  if (!['GET', 'HEAD'].includes(String(method || 'GET').toUpperCase())) return false;
  if (status !== 503 || data?.code !== 'API_STARTING') return false;
  try {
    const target = new URL(url, origin);
    return target.origin === origin && target.pathname.startsWith('/api/');
  } catch {
    return false;
  }
};

const wait = (milliseconds, signal) => new Promise((resolve, reject) => {
  const abort = () => {
    clearTimeout(timer);
    signal?.removeEventListener('abort', abort);
    reject(new DOMException('Request aborted', 'AbortError'));
  };
  const timer = setTimeout(() => {
    signal?.removeEventListener('abort', abort);
    resolve();
  }, milliseconds);
  signal?.addEventListener('abort', abort, { once: true });
  if (signal?.aborted) abort();
});

export const installStartupRetry = (axios, target = window, options = {}) => {
  if (target[FLAG]) return;
  target[FLAG] = true;
  const origin = target.location.origin;
  const sleep = options.sleep || wait;
  const now = options.now || Date.now;
  const limit = options.limit ?? LIMIT;
  const windowMs = options.windowMs ?? WINDOW_MS;

  axios.interceptors.response.use((response) => response, async (error) => {
    const config = error.config;
    const response = error.response;
    if (!config || response?.status !== 503 || response?.data?.code !== 'API_STARTING') throw error;
    let url;
    try { url = axios.getUri(config); } catch { throw error; }
    if (!isStartupRead(config.method, url, origin, response.status, response.data)) throw error;
    const count = config.__conceitoStartupRetries || 0;
    const deadline = config.__conceitoStartupDeadline ?? now() + windowMs;
    if (count >= limit || now() + DELAY_MS > deadline || config.signal?.aborted) throw error;
    await sleep(DELAY_MS, config.signal);
    if (config.signal?.aborted) throw new axios.CanceledError('Request aborted');
    if (now() >= deadline) throw error;
    return axios.request({ ...config, __conceitoStartupRetries: count + 1,
      __conceitoStartupDeadline: deadline });
  });

  const originalFetch = target.fetch.bind(target);
  target.fetch = async (input, init = {}) => {
    const method = init.method || input?.method || 'GET';
    const url = typeof input === 'string' ? input : input?.url || String(input);
    const signal = init.signal || input?.signal;
    let response = await originalFetch(input, init);
    const deadline = now() + windowMs;
    for (let count = 0; count < limit && now() + DELAY_MS <= deadline; count += 1) {
      if (response.status !== 503) break;
      let data;
      try { data = await response.clone().json(); } catch { break; }
      if (!isStartupRead(method, url, origin, response.status, data)) break;
      await sleep(DELAY_MS, signal);
      if (signal?.aborted) throw new DOMException('Request aborted', 'AbortError');
      if (now() >= deadline) break;
      response = await originalFetch(input, init);
    }
    return response;
  };
};
